/**
 * Browser side effects for the test timers: a short chime through Web Audio and an OS notification. Both are best
 * effort and fail silently: the countdown on screen and the running-timers bar never depend on them. Nothing here
 * receives or shows patient information.
 *
 * No screen wake lock: this is the desktop and laptop browser app (owner decision 2026-11-04; the iPads get a native
 * app), and on a clinic computer a wake lock also holds off the screen saver and the computer's own lock after
 * inactivity, a control the practice may rely on, for the whole test with a record on screen. The chime, the bar and
 * the notification do not need the screen on.
 */
import { notificationFor, type TimerCues } from "./timers";

export type NotifyState = NotificationPermission | "unsupported";

export function notifyState(): NotifyState {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
}

/**
 * Asks to show notifications. Call it synchronously inside the click handler, before any await: WebKit honours the
 * request only during the user's gesture. Older Safari takes a callback and returns nothing; newer browsers return a
 * promise; both are handled.
 */
export function requestNotify(): Promise<NotifyState> {
  if (typeof Notification === "undefined") return Promise.resolve("unsupported");
  if (Notification.permission !== "default") return Promise.resolve(Notification.permission);
  return new Promise((resolve) => {
    try {
      const result = Notification.requestPermission((p) => resolve(p)) as Promise<NotificationPermission> | undefined;
      if (result && typeof result.then === "function") result.then(resolve, () => resolve(notifyState()));
    } catch {
      resolve(notifyState());
    }
  });
}

let audio: AudioContext | null = null;

/** Creates or resumes the audio context inside the "Start timer" click, which is what lets a later chime play. */
export function unlockAudio(): void {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    audio ??= new Ctor();
    void audio.resume().catch(() => undefined);
    // A one-sample silent buffer started during the gesture unlocks output on WebKit.
    const source = audio.createBufferSource();
    source.buffer = audio.createBuffer(1, 1, 22_050);
    source.connect(audio.destination);
    source.start(0);
  } catch {
    /* no Web Audio: the visual cue and the notification remain */
  }
}

/** Three short tones. Does nothing if the audio was never unlocked in this tab. */
export function chime(): void {
  if (!audio) return;
  try {
    if (audio.state === "suspended") void audio.resume().catch(() => undefined);
    const t0 = audio.currentTime + 0.05;
    [880, 880, 1175].forEach((hz, i) => {
      const osc = audio!.createOscillator();
      const gain = audio!.createGain();
      const at = t0 + i * 0.32;
      osc.type = "sine";
      osc.frequency.value = hz;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.35, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.26);
      osc.connect(gain).connect(audio!.destination);
      osc.start(at);
      osc.stop(at + 0.28);
    });
  } catch {
    /* ignore */
  }
}

function osNotify(title: string, body: string, tag: string): void {
  if (notifyState() !== "granted") return;
  try {
    const n = new Notification(title, { body, tag });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    /* some browsers allow notifications only from a service worker */
  }
}

/** The cues the clinic's timer store uses in the browser. */
export const browserCues: TimerCues = {
  due(read) {
    chime();
    const { title, body } = notificationFor(read);
    osNotify(title, body, read.tag);
  },
};
